import { useState } from 'react';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import { OrgStatus } from '@constants/generic';
import useESign from '@features/e-sign/hooks/useESign';
import SubmitKycModal from '@features/subscription/components/SubmitKycModal';

import VerificationContentCard from './VerificationContentCard';

interface AccountReadinessTimelineProps {
  selfDeclarationStatus: string;
  orgStatus: OrgStatus;
  orgId: string;
}

const AccountReadinessTimeline = ({
  selfDeclarationStatus,
  orgStatus,
  orgId
}: AccountReadinessTimelineProps) => {
  const [isKycModalOpen, setIsKycModalOpen] = useState(false);
  const { handleESign, isLoading: isESignLoading } = useESign(orgId);

  const isVerificationCompleted =
    selfDeclarationStatus !== '' && orgStatus !== OrgStatus.REJECTED;

  const getVerificationIcon = () => {
    if (isVerificationCompleted) return 'verified';
    if (orgStatus === OrgStatus.REJECTED) return 'warning-red';
    return 'warning-orange';
  };

  const getDeclarationIcon = () => {
    if (isVerificationCompleted) return 'file-green';
    return 'file-grey';
  };

  const onClickResubmit = () => {
    setIsKycModalOpen(true);
  };

  return (
    <div
      className={`relative mt-4 flex flex-col ${
        isVerificationCompleted ? '' : 'gap-4'
      }`}
    >
      {/* Step 1: Verification */}
      {!isVerificationCompleted && (
        <div
          className={`absolute left-4 top-0 h-full w-[2px]
         bg-gradient-to-b to-cofeeGreyBlue300
         [mask:repeating-linear-gradient(to_bottom,#000_0_8px,transparent_8px_14px)]
         ${
           orgStatus === OrgStatus.REJECTED
             ? 'from-cofeeRed6'
             : 'from-cofeeOrange300'
         }`}
        />
      )}

      <div className="flex items-start gap-3">
        <div className="z-10 flex shrink-0 items-center">
          <Icon name={getVerificationIcon()} size="36" />
        </div>
        <VerificationContentCard
          isVerificationCompleted={isVerificationCompleted}
          orgStatus={orgStatus}
          onClickResubmit={onClickResubmit}
        />
      </div>

      {isVerificationCompleted && (
        <div
          className="ml-4 h-11 w-[2px]
         bg-gradient-to-b from-cofeeGreyBlue100 to-cofeePrimary6
         [mask:repeating-linear-gradient(to_bottom,#000_0_8px,transparent_8px_14px)]"
        />
      )}

      {/* Step 2: Self-declaration */}
      <div className="flex items-start gap-3">
        <div className="z-10 flex shrink-0 items-center">
          <Icon name={getDeclarationIcon()} size="36" />
        </div>
        {(selfDeclarationStatus === '' || orgStatus === OrgStatus.REJECTED) && (
          <div className="flex items-center pt-2">
            <span className="text-base font-medium text-cofeeNeutral6">
              Complete self-declaration form
            </span>
          </div>
        )}
        {selfDeclarationStatus === 'initiated' &&
          orgStatus !== OrgStatus.REJECTED && (
            <div className="rounded-xl border border-grey90 p-4 shadow-boxBorderShadow">
              <div className="text-base font-medium text-cofeeNeutral10">
                Complete self-declaration form
              </div>
              <div className="mt-1 text-sm text-cofeeNeutral7">
                Please complete the self declaration form to start collecting
                fees
              </div>
              <Button
                handleClick={handleESign}
                variant="primary"
                label="Proceed"
                className="mt-3 min-w-30"
                loading={isESignLoading}
                disabled={isESignLoading}
              />
            </div>
          )}
        {selfDeclarationStatus === 'submitted' &&
          orgStatus !== OrgStatus.REJECTED && (
            <div className="rounded-xl border border-grey90 p-4 shadow-boxBorderShadow">
              <div className="text-base font-medium text-cofeeNeutral10">
                Self-declaration form submitted!
              </div>
              <div className="mt-1 text-sm text-cofeeNeutral7">
                The self-declaration form has been submitted successfully !
              </div>
            </div>
          )}
      </div>
      {isKycModalOpen && (
        <SubmitKycModal
          onClose={() => setIsKycModalOpen(false)}
          isDocumentFailed
        />
      )}
    </div>
  );
};

export default AccountReadinessTimeline;
